import compact from "lodash/compact";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { ActionNotPossibleContent } from "@/components/Interface/HintTooltip/ActionNotPossibleContent";
import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";
import { ESimInfoEsimResetNotAllowedReasonItem } from "@/services/model";
import { NamespaceKey } from "@/utils/translationKey";

type PossibleReasons = ESimInfoEsimResetNotAllowedReasonItem;

const reasonKey: Partial<
  Record<
    PossibleReasons,
    NamespaceKey<
      "portal",
      "cockpit.managementTile.details.sections.details.reasons"
    >
  >
> = {
  [ESimInfoEsimResetNotAllowedReasonItem.SimSuspended]: "simSuspended",
  [ESimInfoEsimResetNotAllowedReasonItem.SimInactive]: "simInactive",
  [ESimInfoEsimResetNotAllowedReasonItem.NoMoreActions]: "noMoreActions",
  [ESimInfoEsimResetNotAllowedReasonItem.WrongSimType]: "wrongSimType",
  [ESimInfoEsimResetNotAllowedReasonItem.NoAutoRenew]: "noAutoRenew",
  [ESimInfoEsimResetNotAllowedReasonItem.SimNoActivePackage]:
    "simNoActivePackage",
  [ESimInfoEsimResetNotAllowedReasonItem.SimTerminated]: "simTerminated",
  // Cancelled and terminated read the same to the customer.
  [ESimInfoEsimResetNotAllowedReasonItem.SimCancelled]: "simTerminated",
  [ESimInfoEsimResetNotAllowedReasonItem.StatusNotAllowed]: "StatusNotAllowed",
  [ESimInfoEsimResetNotAllowedReasonItem.SimFixedService]: "simFixedService",
  [ESimInfoEsimResetNotAllowedReasonItem.PackageOrderInProgress]:
    "packageOrderInProgress",
};

interface Props {
  dataTestid: string;
  disabledButtonText: string;
  reasons: PossibleReasons[];
}

// The following tooltip is needed to let the user know why the functionality in their reload/transfer button
// is disabled. It reads from an array of reasons from the BE endpoint EsimInfoEsimActionNotAllowed and once the user
// and appears once the user tired clicking on the buttons, thus helping them know why the function is unavailable.
export const EsimDisabledActionsTooltip: FC<Props> = ({
  dataTestid,
  disabledButtonText,
  reasons,
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details.reasons",
  });

  const translatedReasons = compact<string>(
    reasons.map((reason) =>
      reasonKey[reason] ? t(reasonKey[reason]) : undefined,
    ),
  );

  return (
    <HintTooltip
      dataTestid={`${dataTestid}-hint`}
      align="right"
      content={<ActionNotPossibleContent reasons={translatedReasons} />}
    >
      <Button dataTestid={dataTestid} size="sm" variant="primary" disabled>
        {disabledButtonText}
      </Button>
    </HintTooltip>
  );
};
